import { BaseElementController, ElementController } from "elements/base/controller";
import { BoardContext } from "..";
import { MondayTaskElement } from "./schema";
import { equals } from "rambda";
import { MondayErrorCodes, MondayItemData } from "shared/integrations/monday-types";
import {
  MondayTaskSection,
  calculateMondayTaskHeight,
  MONDAY_TASK_SECTION_HEIGHTS,
  MONDAY_TASK_COLUMN_DETAILS_ROW_HEIGHT,
  MONDAY_TASK_UPDATES_ROW_HEIGHT,
  MONDAY_TASK_UPDATES_HEADER_HEIGHT,
  MONDAY_TASK_DYNAMIC_SECTION_PADDING,
  MONDAY_TASK_UPDATES_SHOW_MORE_HEIGHT,
  MONDAY_TASK_FILES_ROW_HEIGHT,
  MONDAY_TASK_PINNED_COLUMNS_HEIGHT,
} from "shared/datamodel/monday-task";
import { MondayColumn } from "shared/integrations/mapper";
import { COLUMN_BLACKLIST, FONT_SIZE, FONT_FAMILY, PADDING } from "./consts";
import { ControllerCache, type MondayItemColumn } from "./memoization-cache";
import { measureTextHeight } from "./text-utils";
import { calculatePinnedColumns } from "./utils";
export type { MondayItemColumn } from "./memoization-cache";

export type FileColumn = {
  id: string;
  type: "file";
  value: {
    extension: string;
    name: string;
    id: string;
    isImage: boolean;
    url: string;
  }[];
};

type MutateFunction = (
  colId: string,
  colValue: unknown,
  columnDefinition: { id: string; type: string }
) => Promise<void>;

export interface IMondayTaskController extends ElementController<MondayTaskElement> {
  setBoardColumns(columns: MondayColumn[]): void;
  getBoardColumns(): MondayColumn[] | undefined;
  setItem(item?: MondayItemData | null): void;
  getItem(): MondayItemData | null;
  setMutateFunction(mutate: MutateFunction): void;
  updateColumnValue(colId: string, colValue: unknown, columnDefinition: { id: string; type: string }): Promise<void>;

  isLoading(): boolean;
  isError(): boolean;
  getError(): { code: MondayErrorCodes; url?: string } | null;
  setError(error: MondayErrorCodes | null, url?: string): void;

  toggleExpanded(): void;
  toggleShowAllUpdates(): void;
  toggleUpdateExpansion(updateId: string): void;

  displayedUpdates(): MondayItemData["updates"];
  hasMoreUpdates(): boolean;
  getUpdateHeight(update: MondayItemData["updates"][0]): number;
  getUpdateYPositions(): number[];
  getShowMoreY(): number;

  breadcrumbs(): string[] | null;
  pinnedColumns(): MondayItemColumn[];
  detailColumns(): MondayItemColumn[];
  hasExpandableContent(): boolean;
  imagePreviews(): string[];
  files(): (FileColumn["value"][0] & { hidden: boolean })[];
  toggleFileVisibility(fileId: string): void;
  updateHeight(): void;
  getSectionY(section: MondayTaskSection): number;
  getBorderColor(): string;
}

export default class MondayTaskController
  extends BaseElementController<MondayTaskElement>
  implements IMondayTaskController
{
  private mutateFunction?: MutateFunction;
  private boardColumns?: MondayColumn[];
  private item?: MondayItemData;
  private loading = true;
  private error: { code: MondayErrorCodes; url?: string } | null = null;
  private cache = new ControllerCache();

  public setBoardColumns(columns: MondayColumn[]) {
    if (equals(this.boardColumns, columns)) return;
    this.boardColumns = columns;
    this.notify(["board-columns-changed"]);
  }

  public getBoardColumns() {
    return this.boardColumns;
  }

  private getItemColumns(): MondayItemColumn[] {
    if (!this.item) return [];
    return (this.boardColumns ?? []).map((column) => {
      const value = this.item?.columnValues?.find((c) => c.id === column.id)?.value ?? null;
      return { ...column, value };
    });
  }

  public setItem(item?: MondayItemData | null) {
    if (equals(this.item, item)) return;

    this.loading = !item;
    this.item = item ?? undefined;
    this.notify(["item-changed"]);
  }

  public getItem() {
    return this.item ?? null;
  }

  public setMutateFunction(mutate: MutateFunction) {
    this.mutateFunction = mutate;
  }

  public updateColumnValue: MutateFunction = async (colId, colValue, columnDefinition) => {
    if (!this.mutateFunction) {
      throw new Error("Mutate function not set on controller");
    }
    return this.mutateFunction(colId, colValue, columnDefinition);
  };

  public isLoading(): boolean {
    return this.loading;
  }

  public isError(): boolean {
    return this.error !== null;
  }

  public getError(): { code: MondayErrorCodes; url?: string } | null {
    return this.error;
  }

  public setError(error: MondayErrorCodes | null, url?: string): void {
    const newError = error === null ? null : { code: error, ...(url && { url }) };
    if (equals(this.error, newError)) return;
    this.error = newError;
    if (error !== null) this.loading = false;
    this.notify();
  }

  public toggleExpanded() {
    this.patchElement(this.id, (draft: MondayTaskElement) => {
      draft.expanded = !draft.expanded;
    });
  }

  public toggleShowAllUpdates() {
    this.patchElement(this.id, (draft: MondayTaskElement) => {
      draft.showAllUpdates = !draft.showAllUpdates;
    });
  }

  /**
   * Returns the updates to display in the updates section.
   * Shows first 3 updates by default, or all updates if showAllUpdates is true.
   */
  public displayedUpdates(): MondayItemData["updates"] {
    if (!this.item) return [];

    const item = this.item;
    return this.cache.getDisplayedUpdates([item.updates, this.element.showAllUpdates], () => {
      const updates = item.updates;
      const showAll = this.element.showAllUpdates ?? false;
      // Show up to 3 updates, or all if showAllUpdates is enabled
      return showAll ? updates : updates.slice(0, 3);
    });
  }

  public hasMoreUpdates(): boolean {
    if (!this.item) return false;

    const totalUpdates = this.item.updates.length;
    return this.cache.getHasMoreUpdates([totalUpdates], () => {
      return totalUpdates > 3;
    });
  }

  public toggleUpdateExpansion(updateId: string): void {
    this.patchElement(this.id, (draft: MondayTaskElement) => {
      draft.expandedUpdateId = draft.expandedUpdateId === updateId ? null : updateId;
    });
  }

  /**
   * Calculate the height needed for a single expanded update.
   * Uses Konva's accurate text measurement to determine height based on actual text wrapping.
   */
  private getExpandedUpdateHeight(updateText: string): number {
    return this.cache.getUpdateHeight(updateText, updateText, () => {
      // Layout constants
      const MONDAY_TASK_WIDTH = 275;
      const AVATAR_SIZE = 18;
      const NAME_X = AVATAR_SIZE + 8;
      const availableWidth = MONDAY_TASK_WIDTH - PADDING * 2 - NAME_X - 6;

      // Measure actual text dimensions using Konva
      const { height: textHeight } = measureTextHeight(updateText, {
        fontSize: FONT_SIZE,
        fontFamily: FONT_FAMILY,
        fontStyle: "400",
        maxWidth: availableWidth,
        lineHeight: 1.2,
        wrap: "word",
      });

      // Total height: top padding + measured text height
      return MONDAY_TASK_UPDATES_ROW_HEIGHT + textHeight;
    });
  }

  /**
   * Get the height for a specific update, accounting for expansion state
   */
  public getUpdateHeight(update: MondayItemData["updates"][0]): number {
    if (this.element.expandedUpdateId === update.id) {
      return this.getExpandedUpdateHeight(update.text);
    }
    return MONDAY_TASK_UPDATES_ROW_HEIGHT;
  }

  /**
   * Get the Y positions for all displayed updates, accounting for variable heights
   */
  public getUpdateYPositions(): number[] {
    const updates = this.displayedUpdates();
    return this.cache.getUpdateYPositions([updates, this.element.expandedUpdateId], () => {
      const positions: number[] = [];
      let cumulativeY = 0;

      for (const update of updates) {
        positions.push(cumulativeY);
        const updateHeight = this.getUpdateHeight(update);
        cumulativeY += updateHeight;
      }

      return positions;
    });
  }

  /**
   * Get the Y position for the show more button
   */
  public getShowMoreY(): number {
    const updates = this.displayedUpdates();
    const positions = this.getUpdateYPositions();

    return this.cache.getShowMoreY([positions, updates, this.element.expandedUpdateId], () => {
      const HEADER_HEIGHT = MONDAY_TASK_UPDATES_HEADER_HEIGHT;

      if (positions.length === 0) {
        return HEADER_HEIGHT;
      }

      const lastUpdateY = positions[positions.length - 1];
      const lastUpdate = updates[updates.length - 1];
      const lastUpdateHeight = this.getUpdateHeight(lastUpdate);
      return HEADER_HEIGHT + lastUpdateY + lastUpdateHeight;
    });
  }

  private getUpdatesHeight(): number {
    if (!this.item) return 0;

    const totalUpdates = this.item.updates.length;
    const updates = this.displayedUpdates();
    if (updates.length === 0) return 0;

    const expandedUpdateId = this.element.expandedUpdateId;
    let combinedHeight = MONDAY_TASK_UPDATES_HEADER_HEIGHT + MONDAY_TASK_DYNAMIC_SECTION_PADDING;

    // Calculate height for each displayed update
    for (const update of updates) {
      if (expandedUpdateId === update.id) {
        // Expanded update: use measured height
        combinedHeight += this.getExpandedUpdateHeight(update.text);
      } else {
        // Collapsed update: use fixed row height
        combinedHeight += MONDAY_TASK_UPDATES_ROW_HEIGHT;
      }
    }

    // Show "Show more/less" button if there are more than 3 updates
    if (totalUpdates > 3) {
      combinedHeight += MONDAY_TASK_UPDATES_SHOW_MORE_HEIGHT;
    }

    return combinedHeight;
  }

  public breadcrumbs() {
    if (!this.item) return null;

    const { parent_item, board, group } = this.item;
    if (parent_item) {
      if (!parent_item.board?.name || !parent_item.name) {
        return null;
      }
      return [parent_item.board.name, parent_item.name, "Subitems"];
    } else {
      if (!board?.name || !group?.title) {
        return null;
      }
      return [board.name, group.title];
    }
  }

  /**
   * Returns the pinned columns for the item. Pinned columns are displayed directly on the
   * card, instead of having to expand it to see them.
   */
  public pinnedColumns(): MondayItemColumn[] {
    if (!this.item || !this.boardColumns) return [];

    return this.cache.getPinnedColumns([this.boardColumns, this.item?.columnValues, this.element.pinnedColumns], () => {
      return calculatePinnedColumns(this.getItemColumns(), this.element.pinnedColumns);
    });
  }

  private hiddenColumns(): MondayItemColumn[] {
    if (!this.item || !this.boardColumns) return [];

    return this.cache.getHiddenColumns([this.boardColumns, this.element.hiddenColumns, this.item?.columnValues], () => {
      const hiddenColumnIds = new Set(this.element.hiddenColumns ?? []);
      return this.getItemColumns().filter((col) => hiddenColumnIds.has(col.id));
    });
  }

  /**
   * Returns the columns that should be displayed in the column details section.
   * This excludes pinned columns and any columns in the blacklist.
   */
  public detailColumns(): MondayItemColumn[] {
    if (!this.item || !this.boardColumns) return [];

    return this.cache.getDetailColumns(
      [this.boardColumns, this.item?.columnValues, this.element.pinnedColumns, this.element.hiddenColumns],
      () => {
        const pinnedColumnIds = new Set(this.pinnedColumns().map((col) => col.id));
        const hiddenColumnIds = new Set(this.hiddenColumns().map((col) => col.id));
        return this.getItemColumns().filter(
          (col) => !pinnedColumnIds.has(col.id) && !hiddenColumnIds.has(col.id) && !COLUMN_BLACKLIST.has(col.type)
        );
      }
    );
  }

  public imagePreviews(): string[] {
    if (!this.item?.columnValues) return [];

    const columnValues = this.item.columnValues;
    return this.cache.getImagePreviews([columnValues, this.element.hiddenFiles], () => {
      const fileColumns = columnValues.filter((columnValue) => columnValue.type === "file") as FileColumn[];
      const imageValues = fileColumns.flatMap((columnValue) => {
        return columnValue.value.filter((fileValue) => fileValue.isImage);
      });

      const hiddenFilesSet = new Set(this.element.hiddenFiles ?? []);
      return imageValues
        .filter((imageValue) => !hiddenFilesSet.has(imageValue.id))
        .map((imageValue) => imageValue.url)
        .filter((url) => !!url);
    });
  }

  private getColumnDetailsHeight(): number {
    if (!this.item) return 0;

    const columns = this.detailColumns();
    return columns.length * MONDAY_TASK_COLUMN_DETAILS_ROW_HEIGHT + MONDAY_TASK_DYNAMIC_SECTION_PADDING;
  }

  public updateHeight(): void {
    if (!this.item) return;

    const requiredHeight = this.calculateRequiredHeight();
    if (this.element.height !== requiredHeight) {
      // We do not allow undoing the height change as it is automatic
      this.patchElementWithoutUndo(this.id, (draft: MondayTaskElement) => {
        draft.height = requiredHeight;
      });
    }
  }

  /**
   * Calculate the Y coordinate for a given section within the Monday task card.
   * The Y position is determined by the accumulated heights of all sections that come before it.
   * Sections are ordered as: IMAGE (if present) -> BASE -> PINNED_COLUMNS (if present) -> COLUMN_DETAILS (if expanded) -> FILES (if expanded) -> UPDATES (if expanded)
   */
  public getSectionY(section: MondayTaskSection): number {
    if (!this.item) return 0;

    const visibleSections = this.getVisibleSections();
    const sectionOrder = [
      MondayTaskSection.IMAGE,
      MondayTaskSection.BASE,
      MondayTaskSection.PINNED_COLUMNS,
      MondayTaskSection.COLUMN_DETAILS,
      MondayTaskSection.FILES,
      MondayTaskSection.UPDATES,
    ];

    const sectionYMap = this.cache.getSectionYPositions(
      [
        visibleSections,
        this.element.expandedUpdateId,
        this.element.pinnedColumns,
        this.element.hiddenColumns,
        this.boardColumns,
        this.item?.columnValues,
        this.item?.updates,
      ],
      () => {
        const yPositions = new Map<string, number>();
        let y = 0;

        // Calculate Y for each section by accumulating heights
        for (const currentSection of sectionOrder) {
          yPositions.set(currentSection, y);

          // Only add height if this section is actually visible
          if (visibleSections.includes(currentSection)) {
            switch (currentSection) {
              case MondayTaskSection.COLUMN_DETAILS: {
                y += this.getColumnDetailsHeight();
                break;
              }
              case MondayTaskSection.FILES: {
                y += this.getFilesHeight();
                break;
              }
              case MondayTaskSection.UPDATES: {
                y += this.getUpdatesHeight();
                break;
              }
              case MondayTaskSection.PINNED_COLUMNS: {
                y += MONDAY_TASK_PINNED_COLUMNS_HEIGHT;
                if (this.element.expanded) {
                  y += 4; // Magic design number for Y coord
                }
                break;
              }
              default: {
                y += MONDAY_TASK_SECTION_HEIGHTS[currentSection];
                break;
              }
            }
          }
        }

        return yPositions;
      }
    );

    return sectionYMap.get(section) ?? 0;
  }

  public getBorderColor(): string {
    const defaultColor = "#EBEDF3";

    if (this.element.colorBy && this.item) {
      const column = this.item.columnValues.find((column) => column.id === this.element.colorBy) as
        | { value: { color?: string } }
        | undefined;
      return column?.value?.color ?? defaultColor;
    }

    return this.element.fill ?? defaultColor;
  }

  /**
   * Check if there is any content to show when expanded.
   * Returns false if there are no detail columns and no updates.
   */
  public hasExpandableContent(): boolean {
    return this.detailColumns().length > 0 || this.displayedUpdates().length > 0 || this.files().length > 0;
  }

  public files(): (FileColumn["value"][0] & { hidden: boolean })[] {
    if (!this.item?.columnValues) return [];

    const columnValues = this.item.columnValues;
    return this.cache.getFiles([columnValues, this.element.hiddenFiles], () => {
      const fileColumns = columnValues.filter((columnValue) => columnValue.type === "file") as FileColumn[];
      const allFiles = fileColumns.flatMap((column) => column.value || []);

      const hiddenFilesSet = new Set(this.element.hiddenFiles ?? []);
      return allFiles.map((file) => ({ ...file, hidden: hiddenFilesSet.has(file.id) }));
    });
  }

  private getFilesHeight(): number {
    if (!this.item) return 0;

    const files = this.files();
    if (files.length === 0) return 0;

    const combinedHeight = files.length * MONDAY_TASK_FILES_ROW_HEIGHT + MONDAY_TASK_DYNAMIC_SECTION_PADDING;
    return combinedHeight;
  }

  public toggleFileVisibility(fileId: string): void {
    this.patchElement(this.id, (draft: MondayTaskElement) => {
      draft.hiddenFiles = draft.hiddenFiles?.includes(fileId)
        ? draft.hiddenFiles?.filter((id) => id !== fileId)
        : [...(draft.hiddenFiles ?? []), fileId];
    });
  }

  /**
   * Get the list of visible sections based on the current item data
   */
  private getVisibleSections(): MondayTaskSection[] {
    if (!this.item) return [];

    const item = this.item;
    const cachedSections = this.cache.getVisibleSections(
      [
        item.columnValues,
        item.updates,
        this.element.expanded,
        this.element.pinnedColumns,
        this.element.hiddenColumns,
        this.element.hiddenFiles,
        this.element.showAllUpdates,
        this.element.expandedUpdateId,
        this.boardColumns,
      ],
      () => {
        const sections: MondayTaskSection[] = [];

        // Add image section if there are preview images
        const images = this.imagePreviews();
        if (images.length > 0) {
          sections.push(MondayTaskSection.IMAGE);
        }

        // Base section is always present
        sections.push(MondayTaskSection.BASE);

        // Add pinned columns section if there are any pinned columns
        const pinnedCols = this.pinnedColumns();
        if (pinnedCols.length > 0) {
          sections.push(MondayTaskSection.PINNED_COLUMNS);
        }

        // Add column details section if expanded and there are columns to show
        if (this.element.expanded) {
          const detailColumns = this.detailColumns();
          if (detailColumns.length > 0) {
            sections.push(MondayTaskSection.COLUMN_DETAILS);
          }
        }

        // Add files section if expanded and there are files to show
        if (this.element.expanded) {
          const files = this.files();
          if (files.length > 0) {
            sections.push(MondayTaskSection.FILES);
          }
        }

        // Add updates section if expanded and there are updates to show
        if (this.element.expanded) {
          const updates = this.displayedUpdates();
          if (updates.length > 0) {
            sections.push(MondayTaskSection.UPDATES);
          }
        }

        return sections;
      }
    );

    return cachedSections as MondayTaskSection[];
  }

  private calculateRequiredHeight(): number {
    if (!this.item) return 0;

    const sections = this.getVisibleSections();
    const customHeights = {
      [MondayTaskSection.COLUMN_DETAILS]: this.getColumnDetailsHeight(),
      [MondayTaskSection.UPDATES]: this.getUpdatesHeight(),
      [MondayTaskSection.FILES]: this.getFilesHeight(),
    };
    return calculateMondayTaskHeight(sections, customHeights);
  }

  getLayoutRect(): { x: number; y: number; width: number; height: number } {
    return { x: this.element.x, y: this.element.y, width: this.element.width, height: this.element.height };
  }

  updateContext(context: BoardContext): void {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const makeProps = () => {
      return [this.context.isReadOnly];
    };

    const oldProps = makeProps();
    super.updateContext(context);
    const newProps = makeProps();

    if (!equals(oldProps, newProps)) {
      this.notify();
    }
  }

  updateElement(element: MondayTaskElement): void {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const makeElement = () => ({
      fill: this.element.fill,
      colorBy: this.element.colorBy,
      height: this.element.height,
      expanded: this.element.expanded,
      showAllUpdates: this.element.showAllUpdates,
      expandedUpdateId: this.element.expandedUpdateId,
      hiddenColumns: this.element.hiddenColumns,
      pinnedColumns: this.element.pinnedColumns,
      hiddenFiles: this.element.hiddenFiles,
    });

    const oldElement = makeElement();
    super.updateElement(element);
    const newElement = makeElement();

    if (equals(oldElement, newElement)) {
      return;
    }

    // Make notifications for changed properties
    const notifications = [
      oldElement.fill !== newElement.fill && "fill-changed",
      oldElement.colorBy !== newElement.colorBy && "color-by-changed",
      oldElement.height !== newElement.height && "size-changed",
      oldElement.expanded !== newElement.expanded && "expanded-changed",
      oldElement.showAllUpdates !== newElement.showAllUpdates && "updates-visibility-changed",
      oldElement.expandedUpdateId !== newElement.expandedUpdateId && "update-expansion-changed",
      !equals(oldElement.hiddenColumns, newElement.hiddenColumns) && "hidden-columns-changed",
      !equals(oldElement.pinnedColumns, newElement.pinnedColumns) && "pinned-columns-changed",
      !equals(oldElement.hiddenFiles, newElement.hiddenFiles) && "file-visibility-changed",
    ].filter(Boolean) as string[];

    this.notify(notifications);
  }
}
